import axios, { AxiosRequestConfig, AxiosError } from "axios";
import { ApiMethod } from "@core/interfaces/api.ts";
import { HttpRequestConfig } from "@core/interfaces/http.interface.ts";
import {
  getTokenKeycloak,
  logoutKeycloak,
} from "@core/keycloak/keycloak-config.ts";
import { UserDTO } from "@modules/setting/userManagement/user.model.ts";

export class HttpService {
  protected urlAPI = "";
  protected params = {};
  protected key = getTokenKeycloak();

  /**
   * Méthode pour récupérer ou rafraîchir le token Keycloak
   */
  private async getToken(): Promise<string> {
    try {
      const token = await getTokenKeycloak();

      if (!token) {
        console.warn("No token available. User might be logged out.");
        logoutKeycloak(); // Déconnexion si aucun token n'est disponible
        throw new Error("User is not authenticated.");
      }

      return token;
    } catch (error) {
      console.error("Error retrieving Keycloak token:", error);
      logoutKeycloak(); // Déconnexion en cas d'erreur critique
      throw new Error("Failed to retrieve Keycloak token.");
    }
  }

  async doRequest<T, U = undefined>(
    apiUrl: string,
    method: ApiMethod,
    body?: U,
    config?: HttpRequestConfig
  ): Promise<T> {
    try {
      const token = await this.getToken();
      const headers = {
        ...config?.headers,
        Authorization: `Bearer ${token}`,
      };

      const axiosConfig: AxiosRequestConfig = {
        ...config,
        headers,
        method,
        url: apiUrl,
        data: body,
      };

      const response = await axios(axiosConfig);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          console.warn("Unauthorized request. Logging out...");
          logoutKeycloak(); // Déconnecter en cas de réponse 401
          throw new Error("Unauthorized. User has been logged out.");
        }
        if (error.response?.status === 400) {
          console.warn("Bad Request:", error.response.data);
          throw error.response.data;
        }
        throw this.handleAxiosError(error);
      }
      throw error;
    }
  }

  /**
   * Méthode POST
   */
  async doPost<T, U>(body: U): Promise<T> {
    return this.doRequest<T, U>(this.urlAPI, ApiMethod.POST, body, this.params);
  }

  /**
   * Do GET avec paramètres
   */
  async doGet<T>(): Promise<T> {
    return this.doRequest<T>(this.urlAPI, ApiMethod.GET, undefined, this.params);
  }
  /**
   * Do GET avec les paramètres
   */
  async doGetTerm<T>(user: UserDTO): Promise<T> {
    return this.doRequest<T>(this.urlAPI, ApiMethod.GET, undefined, this.params);
  }
  // original
  // async doGet<T>(): Promise<T> {
  //   return this.doRequest<T>(this.urlAPI, ApiMethod.GET, null, this.params);
  // }

  /**
   * Do PUT request
   */
  async doPut<T, U>(body: U): Promise<T> {
    return this.doRequest<T, U>(this.urlAPI, ApiMethod.PUT, body, this.params);
  }

  /**
   * Do DELETE request
   */
  async doDelete<T>(): Promise<T> {
    return this.doRequest<T>(this.urlAPI, ApiMethod.DELETE, undefined, this.params);
  }

  /**
   * Handle Axios-specific errors
   */
  private handleAxiosError(error: AxiosError): Error {
    if (error.response) {
      // Server responded with a status other than 2xx
      // return new Error(
      //   `HTTP Error: ${error.response.status} - ${error.response.statusText}`
      // );
      return error
    }
    if (error.request) {
      // Request was made but no response received
      return new Error("No response received from the server.");
    }
    // Something else happened
    return new Error(error.message);
  }
}
